import { http } from '../http'
import type { Paginated } from '@/types/api'
import type {
  AdminGroup,
  AdminGroupDetail,
  AdminGroupPayload,
  GroupMemberRolePayload,
  GroupUserCandidate,
  ListGroupsParams,
} from '@/types/group'
import type {
  AssociatedGroupMemberRow,
  AssociationKeyGrants,
  AssociationPermissionsPayload,
  AvailableGroup,
  CreateGroupPayload,
  CreateInviteKeyPayload,
  GroupAssociationRow,
  GroupCandidateRow,
  GroupCandidateStatus,
  GroupDetailResponse,
  GroupInviteKeyRow,
  GroupInviteKeyUseRow,
  GroupMemberRow,
  MissingAliasesReport,
  PendingCandidateGroup,
  UpdateAssociationFlagsPayload,
  UpdateGroupSelfPayload,
} from '@/types/groupPublic'
import type { GroupRankingParams, GroupRankingRow } from '@/types/groupRanking'
import type { GroupTroopsPayload, OverviewCharts, VillagesOverview } from '@/types/overviewVillages'
import type { Group, TacticalRole } from '@/types/user'

/**
 * `POST /groups/select/{id}`. Deselects every other group of the user and
 * invalidates their cache, so the menu must be reloaded afterwards.
 */
export function selectGroup(groupId: number) {
  return http.post<unknown>(`/groups/select/${groupId}`).then((response) => response.data)
}

/** `POST /groups/unselect`. Leaves the user with no active group. */
export function unselectGroup() {
  return http.post<unknown>('/groups/unselect').then((response) => response.data)
}

/** `POST /groups/leave/{id}`. Self-service: any member can leave a group they belong to. */
export function leaveGroup(groupId: number) {
  return http.post<unknown>(`/groups/leave/${groupId}`).then((response) => response.data)
}

/** `GET /groups` — groups visible to the caller. */
export function listGroups() {
  return http.get<Group[]>('/groups').then((response) => response.data)
}

/* ------------------------ self-service, pagina /groups --------------------- */

/**
 * `GET /groups/{id}`. La forma della risposta dipende da chi chiama: solo
 * campi vetrina se non si è membri (né staff), tutto il resto (membri,
 * alleanze, chiave d'invito) altrimenti — vedi `isFullGroupDetail`.
 */
export function getPublicGroupDetail(id: number) {
  return http.get<GroupDetailResponse>(`/groups/${id}`).then((response) => response.data)
}

/** `POST /groups` — creazione self-service. Il fondatore entra come admin. */
export function createGroupSelfService(payload: CreateGroupPayload) {
  return http.post<Group>('/groups', payload).then((response) => response.data)
}

export function updateGroupSelfService(id: number, payload: UpdateGroupSelfPayload) {
  return http.put<Group>(`/groups/${id}`, payload).then((response) => response.data)
}

/**
 * `DELETE /groups/{id}` — self-service, policy `remove` (group_admin del
 * gruppo, oppure admin/editor globale). Irreversibile: cascata a DB su membri,
 * alleanze ammesse, candidature, invite key riutilizzabili e associazioni con
 * altri gruppi.
 */
export function deleteGroupSelfService(id: number) {
  return http.delete<Group>(`/groups/${id}`).then((response) => response.data)
}

/**
 * `GET /groups/available/{server}` — gruppi pubblici di un server a cui non
 * si appartiene ancora, per la sezione "gruppi disponibili" di `/groups`.
 */
export function getAvailableGroups(serverId: number, params: { per_page?: number } = {}) {
  return http
    .get<Paginated<AvailableGroup>>(`/groups/available/${serverId}`, { params })
    .then((response) => response.data)
}

/** `GET /groups/candidates/mine` — gruppi con una candidatura in attesa, su qualsiasi server. */
export function getPendingCandidateGroups() {
  return http
    .get<PendingCandidateGroup[]>('/groups/candidates/mine')
    .then((response) => response.data)
}

export interface ListGroupMembersParams {
  page?: number
  per_page?: number
  sort_by?: 'id' | 'name' | 'player_alias' | 'joined_at' | 'tactical_role' | 'troops_updated_at'
  sort_dir?: 'asc' | 'desc'
  name?: string
  player_alias?: string
  /** `'unassigned'` cerca chi non ha ancora un ruolo tattico assegnato. */
  tactical_role?: TacticalRole | 'unassigned'
  role_admin?: boolean
  role_def?: boolean
  role_off?: boolean
}

/**
 * `GET /groups/users/{id}` — membri paginati, ordinabili e filtrabili
 * (alias/nome utente, ruolo tattico, ruolo interno), self-service per
 * chiunque sia membro del gruppo.
 */
export function getGroupMembers(groupId: number, params: ListGroupMembersParams = {}) {
  // Laravel's `sometimes|boolean` rule accepts 1/0 (and their string forms)
  // but not the literal "true"/"false" strings axios produces by default for
  // JS booleans in query params — same pitfall as `listServers()`, same fix.
  const { role_admin, role_def, role_off, ...rest } = params
  const query: Record<string, unknown> = { ...rest }
  if (role_admin !== undefined) query.role_admin = role_admin ? 1 : 0
  if (role_def !== undefined) query.role_def = role_def ? 1 : 0
  if (role_off !== undefined) query.role_off = role_off ? 1 : 0

  return http
    .get<Paginated<GroupMemberRow>>(`/groups/users/${groupId}`, { params: query })
    .then((response) => response.data)
}

/**
 * `PUT /groups/{group}/members/{user}/tactical-role` — self-service, riservato
 * al capogruppo (`group_admin`). Campo indipendente da admin/def/off.
 */
export function updateMemberTacticalRole(
  groupId: number,
  userId: number,
  tacticalRole: TacticalRole | null,
) {
  return http
    .put<{ tactical_role: TacticalRole | null }>(
      `/groups/${groupId}/members/${userId}/tactical-role`,
      {
        tactical_role: tacticalRole,
      },
    )
    .then((response) => response.data)
}

/** `POST /groups/users/remove/{group}/{user}` — rimozione membro, self-service (`group_admin`). */
export function removeGroupMemberSelf(groupId: number, userId: number) {
  return http
    .post<Group>(`/groups/users/remove/${groupId}/${userId}`)
    .then((response) => response.data)
}

/**
 * `PUT /groups/{group}/members/{user}/role` — ruolo interno (admin/def/off),
 * self-service (`group_admin`). Parziale: solo i campi passati vengono
 * aggiornati.
 */
export function updateMemberGroupRole(
  groupId: number,
  userId: number,
  payload: { admin?: boolean; def?: boolean; off?: boolean },
) {
  return http
    .put<{ admin?: boolean; def?: boolean; off?: boolean }>(
      `/groups/${groupId}/members/${userId}/role`,
      payload,
    )
    .then((response) => response.data)
}

/**
 * `GET /groups/{group}/members/{user}/troops` — situazione truppe di un
 * membro, self-service (`group_admin`). Stessa forma di `getVillagesOverview`,
 * ma sempre limitata a ciò che il membro ha condiviso con QUESTO gruppo (mai
 * le sue dichiarazioni private).
 */
export function getGroupMemberTroopsOverview(groupId: number, userId: number) {
  return http
    .get<VillagesOverview>(`/groups/${groupId}/members/${userId}/troops`)
    .then((response) => response.data)
}

/**
 * `GET /groups/{group}/candidates` — candidature del gruppo per la pagina di
 * revisione del capogruppo, distinta da `getPendingCandidateGroups()` (le
 * "mie" su qualsiasi gruppo).
 */
export function getGroupCandidates(
  groupId: number,
  params: { page?: number; per_page?: number; alias?: string; status?: GroupCandidateStatus } = {},
) {
  return http
    .get<Paginated<GroupCandidateRow>>(`/groups/${groupId}/candidates`, { params })
    .then((response) => response.data)
}

/** `POST /groups/candidates/accept/{group}` — riservato al capogruppo (`group_admin`). */
export function acceptCandidateAdmin(groupId: number, userId: number) {
  return http
    .post<Group>(`/groups/candidates/accept/${groupId}`, { user_id: userId })
    .then((response) => response.data)
}

/** `POST /groups/candidates/refuse/{group}` — riservato al capogruppo (`group_admin`). */
export function refuseCandidateAdmin(groupId: number, userId: number) {
  return http
    .post<Group>(`/groups/candidates/refuse/${groupId}`, { user_id: userId })
    .then((response) => response.data)
}

export interface BulkCandidateResult {
  accepted?: number[]
  refused?: number[]
  failed: { user_id: number; message: string }[]
}

export function bulkAcceptCandidates(groupId: number, userIds: number[]) {
  return http
    .post<BulkCandidateResult>(`/groups/${groupId}/candidates/bulk-accept`, { user_ids: userIds })
    .then((response) => response.data)
}

export function bulkRefuseCandidates(groupId: number, userIds: number[]) {
  return http
    .post<BulkCandidateResult>(`/groups/${groupId}/candidates/bulk-refuse`, { user_ids: userIds })
    .then((response) => response.data)
}

/**
 * `GET /groups/ranking/{id}` — una delle cinque classifiche interne al
 * gruppo, paginata e ricercabile per alias. Il backend applica da solo
 * `ranking_visible`/`ranking_numbers_visible` (401 o valori oscurati): qui non
 * c'è nulla da replicare, solo da rispettare quello che torna.
 */
export function getGroupRanking(groupId: number, params: GroupRankingParams) {
  return http
    .get<Paginated<GroupRankingRow>>(`/groups/ranking/${groupId}`, { params })
    .then((response) => response.data)
}

/**
 * `GET /groups/{group}/troops` — truppe aggregate di tutti i membri del
 * gruppo, una riga per villaggio, per `/group/troops`. Nessun parametro:
 * filtri/ordinamento/totali per tribù restano lato client.
 */
export function getGroupTroops(groupId: number) {
  return http.get<GroupTroopsPayload>(`/groups/${groupId}/troops`).then((response) => response.data)
}

/** `GET /groups/{group}/troops/charts` — le due serie (attacco/difesa) della stessa aggregazione. */
export function getGroupTroopsCharts(groupId: number) {
  return http
    .get<OverviewCharts>(`/groups/${groupId}/troops/charts`)
    .then((response) => response.data)
}

/**
 * `GET /groups/{group}/associations/{association}/troops` — stessa forma di
 * `getGroupTroops`, per il gruppo associato. Il backend risponde con un
 * errore di business (HTTP 200, `error` non zero) se il partner non ha
 * concesso `shows_troops`.
 */
export function getAssociationTroops(groupId: number, associationId: number) {
  return http
    .get<GroupTroopsPayload>(`/groups/${groupId}/associations/${associationId}/troops`)
    .then((response) => response.data)
}

/** `GET /groups/{group}/associations/{association}/troops/charts` — variante grafici. */
export function getAssociationTroopsCharts(groupId: number, associationId: number) {
  return http
    .get<OverviewCharts>(`/groups/${groupId}/associations/${associationId}/troops/charts`)
    .then((response) => response.data)
}

/** `POST /groups/candidates/new/{id}` — candidatura self-service con il player associato. */
export function applyToGroup(groupId: number, note?: string, tacticalRole?: TacticalRole | null) {
  const body: { note?: string; tactical_role?: TacticalRole | null } = {}
  if (note) body.note = note
  if (tacticalRole) body.tactical_role = tacticalRole

  return http
    .post<unknown>(`/groups/candidates/new/${groupId}`, Object.keys(body).length ? body : undefined)
    .then((response) => response.data)
}

/** `POST /groups/candidates/withdraw/{id}` — ritira la PROPRIA candidatura in attesa. */
export function withdrawCandidacy(groupId: number) {
  return http
    .post<unknown>(`/groups/candidates/withdraw/${groupId}`)
    .then((response) => response.data)
}

/**
 * `POST /groups/invite/{id}` — invito classico. Accetta sia la invite_key statica sia una delle KEY riutilizzabili sotto.
 * `tactical_role` è obbligatorio (`TACTICAL_ROLE_REQUIRED` altrimenti, dopo le altre regole d'ingresso);
 * il dual invece lo eredita, vedi `inviteDual`.
 */
export function inviteDirect(groupId: number, key: string, tacticalRole: TacticalRole) {
  return http
    .post<Group>(`/groups/invite/${groupId}`, { key, tactical_role: tacticalRole })
    .then((response) => response.data)
}

/**
 * `POST /groups/alliances/add|remove/{id}` — self-service, riservate al
 * capogruppo (a differenza di `addGroupAlliance`/`removeGroupAlliance` più
 * sotto, che sono la versione `/groups/manage/*` per admin/editor globali).
 */
export function addAllianceSelf(groupId: number, allianceId: number) {
  return http
    .post<Group>(`/groups/alliances/add/${groupId}`, { alliance_id: allianceId })
    .then((response) => response.data)
}

export function removeAllianceSelf(groupId: number, allianceId: number) {
  return http
    .post<Group>(`/groups/alliances/remove/${groupId}`, { alliance_id: allianceId })
    .then((response) => response.data)
}

/**
 * `POST /groups/invite/dual/{id}` — invito "dual", entra con il personaggio dell'invitante e ne eredita
 * il ruolo tattico (nessuna scelta, così i due account dello stesso player non divergono).
 */
export function inviteDual(groupId: number, inviterUserId: number, key: string) {
  return http
    .post<Group>(`/groups/invite/dual/${groupId}`, { user_id: inviterUserId, key })
    .then((response) => response.data)
}

/** Mirror delle regole backend (`GroupController::STEMMA_MAX_KB`, stesse di PROPIC). */
export const STEMMA_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const
export const STEMMA_MAX_BYTES = 5 * 1024 * 1024

/**
 * `POST /groups/{id}/stemma` — multipart, campo `stemma`. Stesso pattern di
 * `uploadPropic`: header esplicito (altrimenti axios serializza in JSON) e
 * timeout più lungo del default per una connessione mobile lenta.
 */
export function uploadGroupStemma(
  groupId: number,
  file: File,
  onProgress?: (percent: number) => void,
) {
  const form = new FormData()
  form.append('stemma', file)

  return http
    .post<Group>(`/groups/${groupId}/stemma`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
      onUploadProgress: onProgress
        ? (event) => {
            if (!event.total) return
            onProgress(Math.round((event.loaded * 100) / event.total))
          }
        : undefined,
    })
    .then((response) => response.data)
}

export function deleteGroupStemma(groupId: number) {
  return http.delete<Group>(`/groups/${groupId}/stemma`).then((response) => response.data)
}

/** `POST /groups/{id}/invite-key/dual/regenerate` — rigenera la PROPRIA chiave "dual". */
export function regenerateDualInviteKey(groupId: number) {
  return http
    .post<{ invite_key_dual: string }>(`/groups/${groupId}/invite-key/dual/regenerate`)
    .then((response) => response.data)
}

/* ------------------------------- admin panel ------------------------------ */

/**
 * Tutto ciò che segue vive sotto `/groups/manage` ed è riservato ad admin ed
 * editor **globali** (non ai capigruppo): sono le operazioni che scavalcano le
 * regole d'ingresso al gruppo.
 */

/** `GET /groups/manage` — elenco paginato con i conteggi di membri e alleanze. */
export function listGroupsForAdmin(params: ListGroupsParams = {}) {
  return http
    .get<Paginated<AdminGroup>>('/groups/manage', { params })
    .then((response) => response.data)
}

/** `GET /groups/manage/{id}` — membri con ruolo e personaggio, alleanze, chiave. */
export function getGroupDetail(id: number) {
  return http.get<AdminGroupDetail>(`/groups/manage/${id}`).then((response) => response.data)
}

/** `POST /groups/manage` — il gruppo nasce **senza membri**. */
export function createGroup(payload: AdminGroupPayload) {
  return http.post<AdminGroup>('/groups/manage', payload).then((response) => response.data)
}

export function updateGroup(id: number, payload: AdminGroupPayload) {
  return http.put<AdminGroup>(`/groups/manage/${id}`, payload).then((response) => response.data)
}

/** `DELETE /groups/manage` — cancellazione multipla. */
export function deleteGroups(ids: number[]) {
  return http
    .delete<{ deleted: number }>('/groups/manage', { data: { ids } })
    .then((response) => response.data)
}

/**
 * `GET /groups/manage/{id}/users/search` — utenti da aggiungere.
 *
 * Sta qui e non su `/users` perché quella è gestione utenti, riservata agli
 * admin: un editor deve poter aggiungere un membro senza le chiavi di tutta
 * l'anagrafica. Gli utenti già nel gruppo non vengono restituiti.
 */
export function searchGroupCandidates(groupId: number, query: string, limit = 20) {
  return http
    .get<GroupUserCandidate[]>(`/groups/manage/${groupId}/users/search`, {
      params: { q: query, limit },
    })
    .then((response) => response.data)
}

/**
 * `POST /groups/manage/{id}/members` — aggiunge un membro.
 *
 * Il personaggio è obbligatorio perché lo è la colonna sul pivot: se l'utente
 * non l'aveva ancora rivendicato, il backend lo rivendica per lui (sostituendo
 * quello che aveva sullo stesso server).
 */
export function addGroupMember(
  groupId: number,
  payload: { user_id: number; player_id: number } & GroupMemberRolePayload,
) {
  return http
    .post<AdminGroupDetail>(`/groups/manage/${groupId}/members`, payload)
    .then((response) => response.data)
}

export function updateGroupMemberRole(
  groupId: number,
  userId: number,
  payload: GroupMemberRolePayload,
) {
  return http
    .put<AdminGroupDetail>(`/groups/manage/${groupId}/members/${userId}`, payload)
    .then((response) => response.data)
}

export function removeGroupMember(groupId: number, userId: number) {
  return http
    .delete<AdminGroupDetail>(`/groups/manage/${groupId}/members/${userId}`)
    .then((response) => response.data)
}

/** `POST /groups/manage/{id}/alliances` — solo alleanze dello stesso server. */
export function addGroupAlliance(groupId: number, allianceId: number) {
  return http
    .post<AdminGroupDetail>(`/groups/manage/${groupId}/alliances`, { alliance_id: allianceId })
    .then((response) => response.data)
}

export function removeGroupAlliance(groupId: number, allianceId: number) {
  return http
    .delete<AdminGroupDetail>(`/groups/manage/${groupId}/alliances/${allianceId}`)
    .then((response) => response.data)
}

/**
 * `POST /groups/manage/{id}/invite-key` — rigenera la chiave.
 *
 * La vecchia smette di funzionare all'istante: è esattamente il motivo per cui
 * la si rigenera.
 */
export function regenerateInviteKey(groupId: number) {
  return http
    .post<{ invite_key: string }>(`/groups/manage/${groupId}/invite-key`)
    .then((response) => response.data)
}

/* ------------------- impostazioni gruppo: invite key riutilizzabili ------------------ */
/*
 * Self-service, riservate al capogruppo (`group_admin`). Distinte dalla
 * `invite_key` statica sopra: coesistono, `inviteDirect()` accetta entrambe.
 */

export function listInviteKeys(groupId: number) {
  return http
    .get<GroupInviteKeyRow[]>(`/groups/${groupId}/invite-keys`)
    .then((response) => response.data)
}

export function createInviteKey(groupId: number, payload: CreateInviteKeyPayload) {
  return http
    .post<GroupInviteKeyRow>(`/groups/${groupId}/invite-keys`, payload)
    .then((response) => response.data)
}

export function deleteInviteKey(groupId: number, inviteKeyId: number) {
  return http
    .delete<{ deleted: boolean }>(`/groups/${groupId}/invite-keys/${inviteKeyId}`)
    .then((response) => response.data)
}

export function getInviteKeyUses(groupId: number, inviteKeyId: number) {
  return http
    .get<GroupInviteKeyUseRow[]>(`/groups/${groupId}/invite-keys/${inviteKeyId}/uses`)
    .then((response) => response.data)
}

/* ---------------------- impostazioni gruppo: associazione gruppi --------------------- */
/* Self-service, riservate al capogruppo (`group_admin`). */

/** Rigenera la PROPRIA chiave di associazione: da dare all'admin di un altro gruppo. */
export function regenerateAssociationKey(groupId: number) {
  return http
    .post<{ association_key: string }>(`/groups/${groupId}/association-key/regenerate`)
    .then((response) => response.data)
}

/**
 * Cosa concede *oggi* la propria chiave a chi la redime — indipendente dal
 * rigenerarla, non tocca le associazioni già create.
 */
export function updateAssociationKeyPermissions(
  groupId: number,
  permissions: AssociationPermissionsPayload,
) {
  return http
    .put<{ key_grants: AssociationKeyGrants }>(`/groups/${groupId}/association-key/permissions`, {
      permissions,
    })
    .then((response) => response.data.key_grants)
}

export function listGroupAssociations(groupId: number) {
  return http
    .get<GroupAssociationRow[]>(`/groups/${groupId}/associations`)
    .then((response) => response.data)
}

/**
 * Redime la chiave di associazione di un altro gruppo, collegandolo a
 * `groupId`. `permissions` sono i permessi che `groupId` (il lato che redime)
 * concede in cambio al proprietario della chiave — quelli che il proprietario
 * concede a `groupId` arrivano dai `key_grants_*` impostati da lui.
 */
export function associateGroup(
  groupId: number,
  key: string,
  permissions: AssociationPermissionsPayload,
) {
  return http
    .post<GroupAssociationRow>(`/groups/${groupId}/associations`, { key, permissions })
    .then((response) => response.data)
}

/** Aggiorna SOLO i propri due flag per questa associazione — mai quelli del partner. */
export function updateGroupAssociationFlags(
  groupId: number,
  associationId: number,
  payload: UpdateAssociationFlagsPayload,
) {
  return http
    .put<GroupAssociationRow>(`/groups/${groupId}/associations/${associationId}`, payload)
    .then((response) => response.data)
}

export function removeGroupAssociation(groupId: number, associationId: number) {
  return http
    .delete<{ deleted: boolean }>(`/groups/${groupId}/associations/${associationId}`)
    .then((response) => response.data)
}

/**
 * `GET /groups/{group}/associations/{association}/members-preview` — membri
 * paginati del gruppo alleato, sola lettura. Il backend rifiuta (errore di
 * business, non 403) se il partner non ha acceso `shows_members` dal suo lato.
 */
export function getAssociatedGroupMembers(
  groupId: number,
  associationId: number,
  params: { page?: number; per_page?: number } = {},
) {
  return http
    .get<Paginated<AssociatedGroupMemberRow>>(
      `/groups/${groupId}/associations/${associationId}/members-preview`,
      { params },
    )
    .then((response) => response.data)
}

/* --------------------------- membri: mappa e alias mancanti -------------------------- */
/* Self-service, riservate al capogruppo (`group_admin`), come il resto di `/group/members`. */

/**
 * `GET /groups/{group}/members/map` — id giocatore dei membri del gruppo,
 * per il toggle mappa (nascosto di default). Non paginato: è l'elenco intero
 * che disegna i pin. Si risolvono poi i villaggi con
 * `villagesApi.getMapVillages`, la stessa chiamata pubblica di `/map`.
 */
export function getGroupMemberMapPlayerIds(groupId: number) {
  return http
    .get<{ player_ids: number[] }>(`/groups/${groupId}/members/map`)
    .then((response) => response.data.player_ids)
}

/**
 * `GET /groups/{group}/associations/{association}/members/map` — stesso
 * consenso `shows_members` della lista membri del gruppo associato.
 */
export function getAssociationMemberMapPlayerIds(groupId: number, associationId: number) {
  return http
    .get<{ player_ids: number[] }>(`/groups/${groupId}/associations/${associationId}/members/map`)
    .then((response) => response.data.player_ids)
}

/**
 * `GET /groups/{group}/members/missing-aliases` — alias delle alleanze
 * ammesse del gruppo (o di quelle dei suoi membri, in fallback) non ancora
 * nel gruppo. Non paginato: è una lista di reclutamento, non una tabella da
 * sfogliare.
 */
export function getMissingAllianceAliases(groupId: number) {
  return http
    .get<MissingAliasesReport>(`/groups/${groupId}/members/missing-aliases`)
    .then((response) => response.data)
}
